import React from "react";
import { clone, filter, map } from "lodash";
import { PreviewLayout } from "./";
import { Typography } from "@components/Typography";
import { Link } from "../Link";
import { IconList } from "@components/IconList";
import { Lazy } from "@/components/Lazy";
import { QREditorContextProps, QRProps } from "@/contexts/QREditorContext/types";
import { QREditorContext } from "@/contexts/QREditorContext";
import { isFileAblob } from "@/utils/qrEditorHelper";
import { checkValidURL } from "@/utils/formHelper";
import { InvalidVideoFormatToast, InvalidProviderToast } from "@/utils/uploadHelper";
import { useToast } from "@/hooks/ui/useToast";
import { Video } from "../Video";
import { useTranslation } from "react-i18next";
import { NAMESPACES } from "@/internals/constants/namespaces";

export default function PreviewVideo(props: QRProps<"video">) {
  const { toast } = useToast();
  const { t: tValidations } = useTranslation(NAMESPACES.VALIDATIONS);
  const qrEditorContext = React.useContext<QREditorContextProps<"video">>(QREditorContext);
  const data = props?.data || qrEditorContext?.qrData.data;
  const isInitPreview = props?.initPreview;

  const [videos, setVideos] = React.useState(
    map(data?.videoUrls, (videoData) => ({ ...videoData, isLoaded: false }))
  );

  const removeVideoIndex = (videoIndex: number) => {
    setVideos(filter(videos, (_, index) => videoIndex !== index));
    qrEditorContext.setQrData({
      ...qrEditorContext.qrData,
      data: {
        ...qrEditorContext.qrData.data,
        videoUrls: filter(
          qrEditorContext.qrData.data.videoUrls,
          (_, index) => videoIndex !== index
        ),
      },
    });
  };

  React.useEffect(() => {
    if (data?.videoUrls) {
      setVideos(map(data?.videoUrls, (videoData) => ({ ...videoData, isLoaded: false })));
    }
  }, [data?.videoUrls]);

  return (
    <PreviewLayout
      page="video"
      dataQA="preview-video-container"
      backgroundColor={data?.themeColor?.backgroundColor}
      includeBrowser
      isInitPreview={isInitPreview}
    >
      <div className="qr-content">
        <Typography
          Tag="p"
          spacing={4}
        >
          {" "}
        </Typography>
        <Typography
          Tag="p"
          classname="u-bold"
          spacing={4}
        >
          {data?.companyName}
        </Typography>
        <Typography
          Tag="p"
          classname="u-semibold"
          size="l"
          style={{ fontSize: "1.25rem", lineHeight: "1.375rem" }}
          spacing={4}
        >
          {data?.title}
        </Typography>
        <Typography
          Tag="p"
          classname=""
          style={{ lineHeight: "1.125rem", letterSpacing: "-0.2px" }}
          spacing={5}
          size="s"
        >
          {data?.description}
        </Typography>
        {data?.buttonText && (
          <Link
            externalLink
            className="button u-spacing-6 u-flex u-flex--center"
            href={checkValidURL(data?.buttonUrl) || ""}
            style={{
              backgroundColor: data?.themeColor?.buttonColor,
              height: "40px",
            }}
            prefetch={false}
          >
            <span
              data-testid="button-label"
              className="button__text body-s"
            >
              {data?.buttonText}
            </span>
          </Link>
        )}

        {isInitPreview && (
          <Lazy placeholderClass="skeleton-placeholder__video__image">
            <img
              src={data?.logoUrl}
              alt="video player"
            />
          </Lazy>
        )}

        {videos?.length > 0 &&
          videos.map((videoUrl, idx) => {
            return (
              <div
                key={videoUrl.name}
                className="video-wrapper"
              >
                <Video
                  key={videoUrl.name}
                  url={videoUrl.url}
                  onReady={(isLoaded) => {
                    const newVideos = clone(videos);
                    newVideos[idx] = { ...videos[idx], isLoaded: isLoaded };
                    setVideos(newVideos);
                  }}
                  onError={(_error) => {
                    const toastContent = isFileAblob(videoUrl.url)
                      ? InvalidVideoFormatToast(tValidations)
                      : InvalidProviderToast(tValidations);

                    toast({
                      type: "custom",
                      content: (_t) => toastContent,
                      marginBottom: 80,
                      duration: 10000,
                    });
                    removeVideoIndex(idx);
                  }}
                />
              </div>
            );
          })}

        {data?.socialNetworks?.length > 0 ? (
          <div className="IconList-wrapper">
            <IconList
              center
              items={data?.socialNetworks}
            />
          </div>
        ) : (
          <div style={{ width: "100%", height: "1px", marginTop: "2.5rem" }}></div>
        )}
      </div>
    </PreviewLayout>
  );
}
